import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, signal, WritableSignal } from '@angular/core';
import { form, FormField, required, requiredError, validate } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  PropertyAvailabilityDto,
  PropertyCurrentMarketingMode,
  PropertySaleUrgency,
} from '@workspace/data-types';
import { AbstractSignalFormComponent } from '@workspace/ng-aura';

export interface PropertyAvailabilityModel {
  isOccupied: boolean | null;
  occupiedUntil: Date | null;
  saleUrgency: PropertySaleUrgency | null;
  isAlreadyOnSale: boolean | null;
  currentMarketingMode: PropertyCurrentMarketingMode | null;
  exclusiveMandateEndDate: Date | null;
}

@Component({
  selector: 'etxe-property-availability',
  imports: [
    CommonModule,
    FormField,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatSelectModule,
  ],
  templateUrl: './availability.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyAvailabilityComponent extends AbstractSignalFormComponent<
  PropertyAvailabilityDto,
  PropertyAvailabilityModel
> {
  PropertySaleUrgency = PropertySaleUrgency;
  PropertyCurrentMarketingMode = PropertyCurrentMarketingMode;

  minDate = new Date();
  minMandateEndDate = new Date();

  constructor() {
    super();
    this.minDate.setDate(this.minDate.getDate() + 1);
    this.minDate.setHours(0, 0, 0, 0);

    this.minMandateEndDate.setHours(0, 0, 0, 0);
  }

  override model: WritableSignal<PropertyAvailabilityModel> = signal<PropertyAvailabilityModel>({
    isOccupied: null,
    occupiedUntil: null,
    saleUrgency: null,
    isAlreadyOnSale: null,
    currentMarketingMode: null,
    exclusiveMandateEndDate: null,
  });

  override formTree = form(this.model, (b) => {
    required(b.saleUrgency);
    validate(b.isAlreadyOnSale, (ctx) => {
      if (ctx.value() === null || ctx.value() === undefined) {
        return requiredError();
      }
      return undefined;
    });
    validate(b.isOccupied, (ctx) => {
      if (ctx.value() === null || ctx.value() === undefined) {
        return requiredError();
      }
      return undefined;
    });
    required(b.occupiedUntil, { when: () => this.model().isOccupied === true });
    required(b.currentMarketingMode, { when: () => this.model().isAlreadyOnSale === true });
    required(b.exclusiveMandateEndDate, {
      when: () =>
        this.model().isAlreadyOnSale === true &&
        this.model().currentMarketingMode === PropertyCurrentMarketingMode.AGENCY_EXCLUSIVE,
    });
  });

  protected override buildModelFromDto(dto: PropertyAvailabilityDto): PropertyAvailabilityModel {
    return {
      isOccupied: dto?.isOccupied ?? null,
      occupiedUntil: dto?.occupiedUntil ? new Date(dto.occupiedUntil) : null,
      saleUrgency: dto?.saleUrgency ?? null,
      isAlreadyOnSale: dto?.isAlreadyOnSale ?? null,
      currentMarketingMode: dto?.currentMarketingMode ?? null,
      exclusiveMandateEndDate: dto?.exclusiveMandateEndDate ? new Date(dto.exclusiveMandateEndDate) : null,
    };
  }

  protected override buildDtoFromModel(model: PropertyAvailabilityModel): PropertyAvailabilityDto {
    return {
      isOccupied: model.isOccupied ?? undefined,
      occupiedUntil: model.isOccupied === true ? (model.occupiedUntil ?? undefined) : undefined,
      saleUrgency: model.saleUrgency ?? undefined,
      isAlreadyOnSale: model.isAlreadyOnSale ?? undefined,
      currentMarketingMode: model.isAlreadyOnSale ? (model.currentMarketingMode ?? undefined) : undefined,
      exclusiveMandateEndDate:
        model.isAlreadyOnSale && model.currentMarketingMode === PropertyCurrentMarketingMode.AGENCY_EXCLUSIVE
          ? (model.exclusiveMandateEndDate ?? undefined)
          : undefined,
    };
  }
}
